import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import colors from 'modules/App/colors';

export type Area = {
  tooltip: React.ReactNode;
  geo: {
    type: string;
    coordinates: [number, number]; // [lng, lat]
  };
  radius?: number;
  onClick?: () => void;
  icon?: string;
  key: string;
  className?: string;
};

export type MapComponentProps = {
  areas: Area[];
  className?: string;
  style?: React.CSSProperties;
};

const defaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.3/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41],
});

const MapComponent: React.FC<MapComponentProps> = ({ areas, className, style }) => {
  const mapRef = useRef<L.Map>(null);

  // Calculate bounds based on all markers
  const bounds = React.useMemo(() => {
    const validAreas = areas.filter((area) => area.geo?.coordinates);
    if (validAreas.length === 0) {
      return L.latLngBounds([
        [48.8566, 2.3522],
        [48.8566, 2.3522],
      ]); // Default to Paris
    }

    return L.latLngBounds(validAreas.map((area) => [area.geo.coordinates[1], area.geo.coordinates[0]]));
  }, [areas]);

  // Fit map to bounds when areas change
  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [bounds]);

  return (
    <div className={className} style={style}>
      <MapContainer
        ref={mapRef}
        center={[48.8566, 2.3522]} // Default center, will be adjusted by bounds
        zoom={5}
        style={{ width: '100%', minHeight: '100%', zIndex: 1 }}
        className="[&_.marker-cluster-small]:bg-primary [&_.marker-cluster-medium]:bg-secondary [&_.marker-cluster-large]:bg-accent"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MarkerClusterGroup
          maxClusterRadius={32}
          polygonOptions={{
            fillColor: 'transparent',
            color: colors.primary,
            weight: 2,
            opacity: 1,
            fillOpacity: 0.8,
          }}
        >
          {areas.map((area, idx) =>
            area.geo?.coordinates ? (
              <Marker
                key={area.key + idx}
                position={[area.geo.coordinates[1], area.geo.coordinates[0]]}
                icon={
                  area.icon
                    ? L.icon({
                        iconUrl: area.icon,
                        iconSize: [48, 48],
                        iconAnchor: [48, 48],
                        popupAnchor: [1, -34],
                        tooltipAnchor: [16, -28],
                        className: area.className,
                      })
                    : defaultIcon
                }
                eventHandlers={{
                  click: area.onClick,
                }}
              >
                <Tooltip sticky>{area.tooltip}</Tooltip>
              </Marker>
            ) : null
          )}
        </MarkerClusterGroup>
      </MapContainer>
    </div>
  );
};

export default MapComponent;
